import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, ArrowRight, Tag, Search } from 'lucide-react';

const News = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newsArticles, setNewsArticles] = useState([]);

  const categories = [
    { id: 'all', name: 'Todas las noticias' },
    { id: 'Convocatoria', name: 'Convocatorias' },
    { id: 'Evento', name: 'Eventos' },
    { id: 'Innovación', name: 'Innovaciones' },
    { id: 'Alianzas', name: 'Alianzas' },
    { id: 'Investigación', name: 'Investigación' },
    { id: 'Capacitación', name: 'Capacitación' },
    { id: 'Tecnología', name: 'Tecnología' },
    { id: 'Reconocimiento', name: 'Reconocimientos' }
  ];

  // Cargar noticias desde JSON
  useEffect(() => {
    fetch('/data/news.json')
      .then(response => response.json())
      .then(data => setNewsArticles(data))
      .catch(error => {
        console.error('Error cargando noticias:', error);
        setNewsArticles([]);
      });
  }, []);

  const filteredNews = newsArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (article.content && article.content.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[400px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="Noticias y comunicación digital"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/65 to-white/75"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="h-20 w-20 text-orange-600 mx-auto mb-6" />
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            <span className="text-orange-600">Noticias</span> y Actualizaciones
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Mantente al día with las últimas novedades, eventos y desarrollos
            en el mundo de la certificación docente internacional
          </p>
        </div>
      </section>      {/* Search and Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar noticias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${selectedCategory === category.id
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-amber-50 hover:text-amber-600'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured News */}
      {!searchTerm && selectedCategory === 'all' && newsArticles.some(article => article.featured) && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">
              Noticias Destacadas
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {newsArticles.filter(article => article.featured).map((article) => (
                <article key={article.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.imageUrl || "https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <Tag className="h-3 w-3 mr-1" />
                        {article.category}
                      </span>
                      <div className="flex items-center text-gray-500 text-sm">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(article.date)}
                      </div>
                      <div className="flex items-center text-gray-500 text-sm">
                        <User className="h-4 w-4 mr-1" />
                        {article.author || 'Equipo GRANA'}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-800 mb-3">
                      {article.title}
                    </h3>

                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>

                    <Link to={`/noticias/${article.id}`} className="inline-flex items-center text-red-800 font-semibold hover:text-red-900 transition-colors duration-200">
                      Leer más
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Regular News Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-800">
              {searchTerm || selectedCategory !== 'all' ? 'Resultados de búsqueda' : 'Últimas Noticias'}
            </h2>
            <span className="text-gray-600">
              {filteredNews.length} artículo{filteredNews.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.filter(article => !article.featured || searchTerm || selectedCategory !== 'all').map((article) => (
              <article key={article.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.imageUrl || "https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"}
                    alt={article.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      {article.category}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {article.author || 'GRANA'}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-800 mb-3 line-clamp-2">
                    {article.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>

                    <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-500 text-sm">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(article.date)}
                    </div>
                    <Link to={`/noticias/${article.id}`} className="text-red-800 font-semibold text-sm hover:text-red-900 transition-colors duration-200">
                      Leer más →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filteredNews.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                No se encontraron noticias que coincidan con tu búsqueda.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl border border-gray-200 p-10 relative overflow-hidden">
            {/* Subtle decorative element */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-50 to-transparent rounded-full -translate-y-32 translate-x-32 opacity-60"></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
              {/* Content */}
              <div>
                <h2 className="text-3xl font-semibold text-gray-900 mb-4 leading-tight">
                  Mantente Informado
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Suscríbete a nuestras actualizaciones para recibir las últimas noticias,
                  tendencias y oportunidades en educación digital.
                </p>

                {/* Services list - more professional */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Boletín mensual especializado</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Alertas de nuevas oportunidades</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Invitaciones a eventos exclusivos</span>
                  </div>
                </div>

                {/* CTA Button */}
                <Link
                  to="/contacto"
                  className="inline-flex items-center px-6 py-3 bg-red-800 text-white font-medium rounded-lg hover:bg-red-900 transition-colors duration-200 shadow-sm hover:shadow-md"
                >
                  Suscribirse al Boletín
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>

              {/* Newsletter Stats */}
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Comunidad Global
                  </h3>
                  <p className="text-sm text-gray-600">
                    Profesionales conectados mundialmente
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Suscriptores activos</span>
                    <span className="text-sm font-semibold text-orange-600">12,000+</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Países representados</span>
                    <span className="text-sm font-semibold text-orange-600">25+ naciones</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Tasa de apertura</span>
                    <span className="text-sm font-semibold text-green-600">89% engagement</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Publicaciones mensuales</span>
                    <span className="text-sm font-semibold text-orange-600">15+ artículos</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default News;